import React, { useState } from 'react';
import { Download, ArrowLeft, Edit3, Image as ImageIcon, Link as LinkIcon, List, BarChart3, Award } from 'lucide-react';
import { AuditInstance, SEOModule } from '../../types';
import { ScoringEngine } from '../../services/scoringEngine';
import { PdfService } from '../../services/pdfService';
import { Button } from '../UI/Button';
import { Badge } from '../UI/Badge';
import {
  getScoreColor,
  getScoreBgColor,
  getScoreBorderColor,
  getScoreLabel,
  hasRoomForImprovement,
  processStepFields,
} from '../../utils/scoreUtils';

interface ReportViewerProps {
  audit: AuditInstance;
  module: SEOModule;
  onNavigateWorkspace: () => void;
  onSaveAudit: (audit: AuditInstance) => void;
}

export const ReportViewer: React.FC<ReportViewerProps> = ({
  audit,
  module,
  onNavigateWorkspace,
  onSaveAudit,
}) => {
  const scores = ScoringEngine.calculateScores(audit, module);

  const [executiveSummary, setExecutiveSummary] = useState(
    audit.customExecutiveSummary ||
      `A comprehensive technical SEO audit was conducted for ${audit.websiteUrl} utilizing Zynex Ninja proprietary enterprise standards. The overall SEO Health Score was evaluated at ${scores.overallScore}%, highlighting strategic growth opportunities across indexation, page titles, meta descriptions, image optimization, link integrity, and domain authority.`
  );

  const [recommendations, setRecommendations] = useState<Record<string, string>>(
    audit.customRecommendations || {}
  );

  const [editingSummary, setEditingSummary] = useState(false);

  const handleSummarySave = () => {
    setEditingSummary(false);
    onSaveAudit({
      ...audit,
      customExecutiveSummary: executiveSummary,
    });
  };

  const handleRecommendationChange = (stepId: string, val: string) => {
    const updated = { ...recommendations, [stepId]: val };
    setRecommendations(updated);
    onSaveAudit({
      ...audit,
      customRecommendations: updated,
    });
  };

  const dateStr = new Date(audit.createdAt).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const formatFieldValue = (val: any): string => {
    if (Array.isArray(val)) {
      return val.join(', ');
    }
    const sVal = String(val);
    switch (sVal) {
      case 'dynamic_regular': return 'Dynamic Site — Active Content Growth (Articles published regularly)';
      case 'static_stalled': return 'Static Site — Stalled Content Growth (Posts dated months/years ago)';
      case 'static_fixed_only': return 'Static Site — Fixed Pages Only (No blog/news section)';
      case 'clear_target': return 'Clear Keyword Target (Consistently present across title/H1/meta)';
      case 'partial_target': return 'Partial Keyword Target (Present in title/H1 but missing in meta/alt)';
      case 'unclear_target': return 'Unclear Keyword Target (Generic terms used; no clear query target)';
      case 'normal_indexation': return 'Normal Indexation (Google index aligns with crawl)';
      case 'under_indexed': return 'Under-Indexed (Google index lower than crawl)';
      case 'over_indexed_params': return 'Over-Indexed (Google index higher due to parameters/attachments)';
      case 'active_linked': return 'Active Social Presence Linked';
      case 'unlinked_broken': return 'Social Icons Present but Links Broken/Unlinked';
      case 'no_social_presence': return 'No Social Media Accounts Found';
      case 'blocked_bypass': return 'Bypass — Siteliner blocked by client firewall/server security';
      case 'unreachable_bypass': return 'Bypass — Website requires login / no server access yet';
      case 'accessible': return 'Accessible — Scanned successfully';
      default: return sVal;
    }
  };

  // Calculate SVG Donut Circle parameters
  const strokeDasharray = 283; // 2 * pi * 45
  const strokeDashoffset = strokeDasharray - (strokeDasharray * scores.overallScore) / 100;
  const totalSteps = module.sections.reduce((acc, sec) => acc + sec.steps.filter(s => s.id !== 'step-meta-keywords').length, 0);

  return (
    <div className="animate-fade-in" style={{ maxWidth: '1080px', margin: '0 auto', padding: '40px 20px' }}>
      {/* Top Controls Bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', flexWrap: 'wrap', gap: '16px' }}>
        <Button variant="secondary" icon={<ArrowLeft size={16} />} onClick={onNavigateWorkspace}>
          Return to Guided Workspace
        </Button>

        <Button
          variant="primary"
          icon={<Download size={18} />}
          onClick={() => PdfService.exportToPdf(audit, module)}
        >
          Export Client PDF Report
        </Button>
      </div>

      {/* Main Report Document Container */}
      <div className="glass-panel" style={{ padding: '40px' }}>
        {/* Cover Title Block */}
        <div style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '28px', marginBottom: '36px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            <Badge variant="lecture">Zynex Ninja Enterprise Framework</Badge>
            <Badge variant="complete">Audit Version: {audit.methodologyVersion}</Badge>
          </div>
          <h1 style={{ fontSize: '34px', color: '#ffffff', fontWeight: 800, marginBottom: '8px', letterSpacing: '-0.5px' }}>
            Comprehensive Technical SEO Audit Report
          </h1>
          <div style={{ fontSize: '18px', color: 'var(--accent-blue)', fontWeight: 600 }}>
            {audit.clientName} ({audit.websiteUrl})
          </div>
          <div style={{ fontSize: '13px', color: 'var(--text-dim)', marginTop: '6px' }}>
            Report Generated on {dateStr}
          </div>
        </div>

        {/* Visual Scorecard Dashboard with Donut Chart & Graphs */}
        <div
          style={{
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.9) 0%, rgba(2, 6, 23, 0.9) 100%)',
            border: '1px solid var(--border-color)',
            borderRadius: 'var(--radius-lg)',
            padding: '30px',
            marginBottom: '40px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.4)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 700, color: 'var(--accent-blue)', textTransform: 'uppercase', marginBottom: '24px' }}>
            <BarChart3 size={18} />
            <span>Executive Performance Scorecard</span>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '30px',
              alignItems: 'center',
            }}
          >
            {/* SVG Donut Ring Gauge for Overall Score */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '24px', justifyContent: 'center' }}>
              <div style={{ position: 'relative', width: '130px', height: '130px' }}>
                <svg width="130" height="130" viewBox="0 0 100 100" style={{ transform: 'rotate(-90deg)' }}>
                  <circle cx="50" cy="50" r="45" stroke="rgba(255,255,255,0.1)" strokeWidth="8" fill="transparent" />
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    stroke={getScoreColor(scores.overallScore)}
                    strokeWidth="8"
                    fill="transparent"
                    strokeDasharray={strokeDasharray}
                    strokeDashoffset={strokeDashoffset}
                    strokeLinecap="round"
                    style={{ transition: 'stroke-dashoffset 1s ease' }}
                  />
                </svg>
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <span style={{ fontSize: '28px', fontWeight: 800, color: '#ffffff', fontFamily: 'var(--font-mono)' }}>
                    {scores.overallScore}%
                  </span>
                  <span style={{ fontSize: '10px', color: 'var(--text-dim)', textTransform: 'uppercase' }}>Overall</span>
                </div>
              </div>

              <div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: '#ffffff', marginBottom: '4px' }}>
                  SEO Health Score
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px' }}>
                  Evaluated across {totalSteps} technical criteria
                </div>
                <div
                  style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 700,
                    background: getScoreBgColor(scores.overallScore),
                    color: getScoreColor(scores.overallScore),
                    border: `1px solid ${getScoreBorderColor(scores.overallScore)}`,
                  }}
                >
                  {getScoreLabel(scores.overallScore)}
                </div>
              </div>
            </div>

            {/* On-Page & Off-Page Sub-score Progress Bars */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', fontWeight: 600, color: '#ffffff', marginBottom: '6px' }}>
                  <span>On-Page Technical Score (66% Weight)</span>
                  <span style={{ color: 'var(--accent-green)', fontFamily: 'var(--font-mono)' }}>{scores.onPageScore}%</span>
                </div>
                <div style={{ height: '8px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', overflow: 'hidden' }}>
                  <div
                    style={{
                      height: '100%',
                      width: `${scores.onPageScore}%`,
                      background: 'linear-gradient(90deg, #10b981 0%, #34d399 100%)',
                      borderRadius: '4px',
                      transition: 'width 1s ease',
                    }}
                  />
                </div>
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', fontWeight: 600, color: '#ffffff', marginBottom: '6px' }}>
                  <span>Off-Page Authority Score (34% Weight)</span>
                  <span style={{ color: 'var(--accent-indigo)', fontFamily: 'var(--font-mono)' }}>{scores.offPageScore}%</span>
                </div>
                <div style={{ height: '8px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', overflow: 'hidden' }}>
                  <div
                    style={{
                      height: '100%',
                      width: `${scores.offPageScore}%`,
                      background: 'linear-gradient(90deg, #6366f1 0%, #818cf8 100%)',
                      borderRadius: '4px',
                      transition: 'width 1s ease',
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Section Score Performance Comparison Bar Graph */}
          <div style={{ marginTop: '30px', paddingTop: '24px', borderTop: '1px solid var(--border-color)' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Section Performance Comparison
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px' }}>
              {module.sections.map(sec => {
                const secScore = scores.sectionScores[sec.id] ?? 100;
                const barColor = getScoreColor(secScore);

                return (
                  <div key={sec.id} style={{ background: 'rgba(2, 6, 23, 0.6)', padding: '10px 14px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-color)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-main)', marginBottom: '6px', fontWeight: 600 }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '140px' }}>{sec.title}</span>
                      <span style={{ color: barColor, fontFamily: 'var(--font-mono)' }}>{secScore}%</span>
                    </div>
                    <div style={{ height: '5px', background: 'rgba(255,255,255,0.1)', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${secScore}%`, background: barColor, borderRadius: '3px' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Table of Contents Section */}
        <div
          style={{
            background: 'rgba(15, 23, 42, 0.6)',
            border: '1px solid var(--border-color)',
            borderRadius: 'var(--radius-md)',
            padding: '24px',
            marginBottom: '40px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '15px', fontWeight: 700, color: '#ffffff', marginBottom: '16px' }}>
            <List size={18} color="var(--accent-blue)" />
            <span>Table of Contents</span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
            {module.sections.map((sec, idx) => {
              const secScore = scores.sectionScores[sec.id] ?? 100;
              return (
                <a
                  key={sec.id}
                  href={`#section-${sec.id}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 14px',
                    borderRadius: 'var(--radius-sm)',
                    background: 'rgba(2, 6, 23, 0.5)',
                    border: '1px solid var(--border-color)',
                    color: 'var(--text-main)',
                    textDecoration: 'none',
                    fontSize: '13px',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--accent-blue)')}
                  onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--border-color)')}
                >
                  <span style={{ fontWeight: 600 }}>{idx + 1}. {sec.title}</span>
                  <span style={{ fontSize: '11px', color: getScoreColor(secScore), fontWeight: 700, fontFamily: 'var(--font-mono)' }}>
                    {secScore}%
                  </span>
                </a>
              );
            })}
          </div>
        </div>

        {/* Executive Summary */}
        <div style={{ marginBottom: '44px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ fontSize: '22px', color: '#ffffff', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Award size={20} color="var(--accent-blue)" />
              <span>Executive Summary</span>
            </h3>
            {!editingSummary ? (
              <button
                onClick={() => setEditingSummary(true)}
                style={{ background: 'none', border: 'none', color: 'var(--accent-blue)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}
              >
                <Edit3 size={14} /> Edit Summary
              </button>
            ) : (
              <Button size="sm" variant="success" onClick={handleSummarySave}>
                Save Summary
              </Button>
            )}
          </div>

          {editingSummary ? (
            <textarea
              className="input-field"
              style={{ minHeight: '130px', width: '100%', fontSize: '14px', lineHeight: 1.7 }}
              value={executiveSummary}
              onChange={(e) => setExecutiveSummary(e.target.value)}
            />
          ) : (
            <div
              style={{
                background: 'rgba(2, 6, 23, 0.6)',
                padding: '22px 26px',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-color)',
                fontSize: '15px',
                color: 'var(--text-main)',
                lineHeight: 1.8,
              }}
            >
              {executiveSummary}
            </div>
          )}
        </div>

        {/* Detailed Audit Sections & Findings */}
        {module.sections.map((section, secIdx) => {
          const sectionScore = scores.sectionScores[section.id] ?? 100;

          return (
            <div key={section.id} id={`section-${section.id}`} style={{ marginBottom: '50px', scrollMarginTop: '20px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderBottom: '2px solid var(--accent-blue)',
                  paddingBottom: '10px',
                  marginBottom: '24px',
                }}
              >
                <h3 style={{ fontSize: '22px', color: '#ffffff', fontWeight: 700 }}>
                  Section {secIdx + 1}: {section.title}
                </h3>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '6px 14px',
                    borderRadius: '20px',
                    fontSize: '13px',
                    fontWeight: 700,
                    background: getScoreBgColor(sectionScore),
                    color: getScoreColor(sectionScore),
                    border: `1px solid ${getScoreBorderColor(sectionScore)}`,
                    fontFamily: 'var(--font-mono)',
                  }}
                >
                  Section Score: {sectionScore}%
                </div>
              </div>

              {section.steps.filter(step => step.id !== 'step-meta-keywords').map((step) => {
                const answer = audit.answers[step.id];
                if (!answer || !answer.completed) {
                  return (
                    <div key={step.id} style={{ fontSize: '13px', color: 'var(--text-dim)', fontStyle: 'italic', marginBottom: '14px', paddingLeft: '10px' }}>
                      {step.stepNumber}. {step.title}: Not completed during audit.
                    </div>
                  );
                }

                const recVal = recommendations[step.id] ?? step.recommendationTemplate ?? '';
                const ev = answer.evidence;
                const { standardFields, urlLists } = processStepFields(step, answer.fieldValues, formatFieldValue);

                return (
                  <div
                    key={step.id}
                    style={{
                      background: 'rgba(15, 23, 42, 0.75)',
                      border: '1px solid var(--border-color)',
                      borderRadius: 'var(--radius-md)',
                      padding: '24px',
                      marginBottom: '20px',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                      <h4 style={{ fontSize: '17px', color: '#ffffff', fontWeight: 600, margin: 0 }}>
                        {step.stepNumber}. {step.title}
                      </h4>
                      <Badge variant="tool">{step.tool}</Badge>
                    </div>

                    {/* Field Metrics & Observations */}
                    {standardFields.length > 0 && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px', marginBottom: '14px' }}>
                        {standardFields.map((field) => (
                          <div key={field.id} style={{ background: '#020617', padding: '10px 14px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-color)' }}>
                            <span style={{ fontSize: '11px', color: 'var(--text-dim)', textTransform: 'uppercase', display: 'block', marginBottom: '2px' }}>
                              {field.label}
                            </span>
                            <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-main)' }}>
                              {field.formattedValue}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Calculated Percentage Ratios */}
                    {Object.entries(answer.calculatedValues).map(([ck, cv]) => {
                      if (ck.endsWith('_display')) {
                        return (
                          <div key={ck} style={{ color: 'var(--accent-blue)', fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>
                            Calculated Ratio: {String(cv)}
                          </div>
                        );
                      }
                      return null;
                    })}

                    {/* Affected URLs List */}
                    {urlLists.map((urlList) => (
                      <div
                        key={urlList.id}
                        style={{
                          background: 'rgba(2, 6, 23, 0.75)',
                          border: '1px solid rgba(56, 189, 248, 0.25)',
                          borderRadius: 'var(--radius-sm)',
                          padding: '14px 18px',
                          margin: '14px 0',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 700, color: 'var(--accent-blue)', textTransform: 'uppercase', marginBottom: '10px' }}>
                          <LinkIcon size={14} />
                          <span>Affected Page URLs — {urlList.label} ({urlList.urls.length})</span>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {urlList.urls.map((urlStr, uIdx) => {
                            const cleanUrl = urlStr.trim();
                            const isHref = cleanUrl.startsWith('http://') || cleanUrl.startsWith('https://');

                            return (
                              <div
                                key={uIdx}
                                style={{
                                  fontSize: '13px',
                                  fontFamily: 'var(--font-mono)',
                                  color: isHref ? 'var(--accent-blue)' : 'var(--text-main)',
                                  wordBreak: 'break-all',
                                }}
                              >
                                {isHref ? (
                                  <a
                                    href={cleanUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: 'var(--accent-blue)', textDecoration: 'none' }}
                                  >
                                    {cleanUrl}
                                  </a>
                                ) : (
                                  <span>{cleanUrl}</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}

                    {/* Editable Recommendation — Rendered only if there is room for improvement */}
                    {hasRoomForImprovement(step, answer, recommendations[step.id]) && (
                      <div style={{ marginTop: '16px' }}>
                        <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--accent-green)', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                          Strategic Recommendation
                        </label>
                        <input
                          type="text"
                          className="input-field"
                          value={recVal}
                          placeholder="Enter strategic recommendation..."
                          onChange={(e) => handleRecommendationChange(step.id, e.target.value)}
                        />
                      </div>
                    )}

                    {/* Verification Screenshot Evidence */}
                    {ev && ev.dataUrl && (
                      <div style={{ marginTop: '18px', borderTop: '1px solid var(--border-color)', paddingTop: '14px' }}>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <ImageIcon size={14} />
                          <span>Verification Evidence ({ev.tool})</span>
                        </div>
                        <img
                          src={ev.dataUrl}
                          alt={ev.caption || step.title}
                          style={{ maxWidth: '100%', maxHeight: '350px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-color)' }}
                        />
                        {ev.notes && (
                          <div style={{ fontSize: '12px', color: 'var(--text-dim)', marginTop: '6px' }}>
                            Notes: {ev.notes}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: '60px', paddingTop: '20px', borderTop: '1px solid var(--border-color)', fontSize: '12px', color: 'var(--text-dim)' }}>
          Generated by Zynex Ninja Enterprise Audit Engine
        </div>
      </div>
    </div>
  );
};
